import { Stack, Table } from '@mui/joy';
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import { FunctionComponent, ReactNode, useMemo, useState } from 'react';

import { TableBody } from '@/components/table/TableBody';
import { TableFooter } from '@/components/table/TableFooter';
import { TableHead } from '@/components/table/TableHead';
import { TableScrollContainer } from '@/components/table/TableScrollContainer';
import { TableSearchFilterWrapper } from '@/components/table/TableSearchFilterWrapper';
import { TableSearchInput } from '@/components/table/TableSearchInput';
import { TableWrapper } from '@/components/table/TableWrapper';
import { usePagination } from '@/hooks/usePagination';

type BackendTableProps<TData extends RowData> = {
  columns: ColumnDef<TData>[];
  getRowId: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  data: TData[] | undefined;
  isFetching: boolean;
  fullHeight?: boolean;
  searchInputPlaceholder: string;
  subject: string;
  onClick: (row: Row<TData>) => void;
  filters?: Record<string, FunctionComponent<{ column: Column<TData> }>>;
  headerButtons?: ReactNode;
};

export function BackendTable<TData extends RowData>({
  columns,
  getRowId,
  data,
  isFetching,
  fullHeight = false,
  searchInputPlaceholder,
  subject,
  onClick,
  filters,
  headerButtons,
}: BackendTableProps<TData>) {
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const defaultData = useMemo(() => [], []);

  const { pagination, onPaginationChange } = usePagination();

  const table = useReactTable({
    data: data ?? defaultData,
    getRowId,
    columns,
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange,

    getCoreRowModel: getCoreRowModel(),

    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <TableWrapper $fullHeight={fullHeight}>
      <TableSearchFilterWrapper $sticky={fullHeight}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <TableSearchInput
            value={globalFilter}
            onChange={(value) => setGlobalFilter(value)}
            placeholder={searchInputPlaceholder}
            isFetching={isFetching}
          />
          {headerButtons}
        </Stack>
      </TableSearchFilterWrapper>

      <TableScrollContainer>
        <Table>
          <TableHead table={table} filters={filters} />
          <TableBody table={table} onClick={onClick} />
        </Table>

        <TableFooter table={table} subject={subject} />
      </TableScrollContainer>
    </TableWrapper>
  );
}
